import { useState, useEffect, useRef, useCallback } from 'react';
import moment from 'moment';
import { RTCCodecAdapter } from '../adapter/rtc-codec';
import { HLSAdapter } from '../adapter/hls';
import { startPlaybackCamera } from '../../../socket/playback';
import { STREAM_MODE } from '../utils';
import { beeXLogger } from '../../../helpers/logger';
import {
  useRetryManager,
  MAX_TRANSCODE_RETRIES,
  MAX_HLS_RETRIES,
  MAX_TRANSCODE_REFETCH,
} from './useRetryManager';
import { detectMediaCodec } from '../../../utils/mediaCodecDetector';

const LOADING_STAGE_LABELS = {
  1: 'Data is being retrieved.',
  2: 'Data is being decoded.',
  3: 'Data is being transferred.',
  4: 'Ready to view.',
  5: 'Switching to compatible stream...',
};

export const useStreamPlayer = ({
  serial,
  namespace,
  autoPlay,
  activeQueryTime,
  selectedDate,
  playerMode,
  isMountedRef,
  isSeekingRef,
  videoRef,
  canvasRef,
  playerRef,
  setVideo,
  video,
  videoDurationRef,
  initialStreamMode,
  filePlayedSecondsRef,
  onDebugLog,
  type = '',
  recordId,
  fromTime,
  toTime,
}) => {
  const [playbackStream, setPlaybackStream] = useState(null);
  const [output, setOutput] = useState(null);
  const [streamMode, setStreamMode] = useState(initialStreamMode || STREAM_MODE.NORMAL);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(null);
  const [videoError, setVideoError] = useState(null);
  const [detectedCodec, setDetectedCodec] = useState(null);

  const adapterRef = useRef(null);
  const lastStreamTokenRef = useRef(null);
  const fetchStreamRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastPlayedSecondsRef = useRef(0);
  const streamModeRef = useRef(initialStreamMode || STREAM_MODE.NORMAL);
  const lastPlayableModeRef = useRef(initialStreamMode || STREAM_MODE.NORMAL);
  const onDebugLogRef = useRef(onDebugLog);

  useEffect(() => {
    onDebugLogRef.current = onDebugLog;
  }, [onDebugLog]);

  const updateStage = useCallback(
    (stage, customText = null) => {
      setLoadingStage(stage);
      const fn = onDebugLogRef.current;
      if (!fn) return;
      const isGatewayOrDvr = type === 'on_gateway' || type === 'on_dvr';
      const isTranscode = streamModeRef.current === STREAM_MODE.TRANSCODE;
      const text = customText || (stage ? LOADING_STAGE_LABELS[stage] : '');

      if (stage === 1) {
        if (isTranscode) {
          fn('transcodeRequest', { status: 'loading', text, stage: 1 });
        } else {
          fn(isGatewayOrDvr ? 'gatewayStream' : 'streamRequest', {
            status: 'loading',
            text,
            stage: 1,
          });
        }
      } else if (stage === 2) {
        if (isTranscode) {
          fn('transcodeRequest', {
            status: 'success',
            text: 'Transcode stream link received',
          });
          fn('transcodeConnect', { status: 'loading', text, stage: 2 });
        } else {
          fn(isGatewayOrDvr ? 'gatewayStream' : 'streamRequest', {
            status: 'success',
            text: 'Stream link received',
          });
          fn(isGatewayOrDvr ? 'gatewayPlayback' : 'streamConnect', {
            status: 'loading',
            text,
            stage: 2,
          });
        }
      } else if (stage === 3) {
        if (isTranscode) {
          fn('transcodeRequest', {
            status: 'success',
            text: 'Transcode stream link received',
          });
          fn('transcodeConnect', { status: 'loading', text, stage: 3 });
        } else {
          fn(isGatewayOrDvr ? 'gatewayStream' : 'streamRequest', {
            status: 'success',
            text: 'Stream link received',
          });
          fn(isGatewayOrDvr ? 'gatewayPlayback' : 'streamConnect', {
            status: 'loading',
            text,
            stage: 3,
          });
        }
      } else if (stage === 4) {
        if (isTranscode) {
          fn('transcodeConnect', {
            status: 'success',
            text: 'Transcode playback ready',
          });
        } else {
          fn(isGatewayOrDvr ? 'gatewayPlayback' : 'streamConnect', {
            status: 'success',
            text: 'Stream playback started',
          });
        }
      } else if (stage === 5) {
        fn(isGatewayOrDvr ? 'gatewayStream' : 'streamRequest', {
          status: 'success',
          text: 'Stream link received',
        });
        fn(isGatewayOrDvr ? 'gatewayPlayback' : 'streamConnect', {
          status: 'error',
          error: 'Normal stream failed (Codec/Decode error)',
        });
        fn('transcodeFallback', {
          status: 'triggered',
          text: 'Switching to compatible stream (H.264)',
        });
      }
    },
    [type],
  );

  const {
    retryRef,
    getErrorKey,
    isSingleRetryNormalError,
    resetNormalMode: resetNormalModeRetry,
    resetAll: resetAllRetry,
    resetTranscode: resetTranscodeState,
  } = useRetryManager();

  const handleProgress = useCallback(
    (playedSeconds) => {
      const dur = videoDurationRef.current;
      if (!dur) return;
      setIsLoading(false);
      lastPlayableModeRef.current = streamModeRef.current;
      retryRef.current = {
        ...retryRef.current,
        hlsCount: 0,
        refetchCount: 0,
      };

      const finalPlayedSeconds = lastPlayedSecondsRef.current + playedSeconds;

      if (finalPlayedSeconds >= dur) {
        setIsLoading(false);
        updateStage(null);
        if (videoRef.current) {
          if (videoRef.current.currentTime !== undefined) {
            videoRef.current.currentTime = dur;
          }
          if (typeof videoRef.current.pause === 'function') {
            videoRef.current.pause();
          }
          videoRef.current.srcObject = null;
        }

        if (playerRef.current) {
          if (typeof playerRef.current.getInternalPlayer === 'function') {
            const internalPlayer = playerRef.current.getInternalPlayer();
            if (internalPlayer && typeof internalPlayer.pause === 'function') {
              internalPlayer.pause();
            }
          } else if (typeof playerRef.current.pause === 'function') {
            playerRef.current.pause();
          }
        }

        if (adapterRef.current) {
          try {
            adapterRef.current.destroy();
          } catch (error) { }
          adapterRef.current = null;
        }
        lastStreamTokenRef.current = null;

        setVideo((p) => ({ ...p, played: 1, ended: true, playing: false }));
        setOutput(null);
        return;
      }

      setVideo((p) => ({
        ...p,
        played: finalPlayedSeconds / dur,
        playing: true,
      }));
    },
    [videoDurationRef, setVideo, videoRef, playerRef, retryRef],
  );

  useEffect(() => {
    streamModeRef.current = streamMode;
  }, [streamMode]);

  const showFinalError = useCallback(
    (errMsg = "Can't play this video") => {
      if (!isMountedRef.current) return;
      isSeekingRef.current = false;
      setIsLoading(false);
      updateStage(null);
      setVideoError("Can't play this video");
      setVideo((p) => ({ ...p, playing: false }));
      const isTranscode = streamModeRef.current === STREAM_MODE.TRANSCODE;
      const isGatewayOrDvr = type === 'on_gateway' || type === 'on_dvr';
      const fn = onDebugLogRef.current;
      if (fn) {
        if (isTranscode) {
          fn('transcodeConnect', {
            status: 'error',
            error: errMsg,
          });
        } else {
          fn(isGatewayOrDvr ? 'gatewayPlayback' : 'streamConnect', {
            status: 'error',
            error: errMsg,
          });
        }
      }
    },
    [isMountedRef, isSeekingRef, setVideo, updateStage, type],
  );

  const resetStreamState = useCallback(
    (caller) => {
      beeXLogger.debug(`Resetting stream state, called by ${caller}`);
      isFetchingRef.current = false;
      resetAllRetry();
      setVideoError(null);
      // setStreamMode(STREAM_MODE.NORMAL);
      setOutput(null);
      setPlaybackStream(null);
      updateStage(null);
      if (adapterRef.current) {
        try {
          adapterRef.current.destroy();
        } catch (_) { }
        adapterRef.current = null;
      }
      lastStreamTokenRef.current = null;
    },
    [resetAllRetry],
  );

  const resetTranscodeRetry = useCallback(() => {
    resetTranscodeState();
    setVideoError(null);
  }, [resetTranscodeState]);

  const resetRetryStateAfterPlaybackStarts = useCallback(() => {
    retryRef.current = {
      ...retryRef.current,
      normalMode: { key: null, retried: false },
      hasTranscodeRetry: false,
      transcodeTimeout: false,
      retryTime: null,
      transcodeCount: 0,
      hlsCount: 0,
      refetchCount: 0,
    };
  }, [retryRef]);

  const markPlaybackStable = useCallback(() => {
    lastPlayableModeRef.current = streamModeRef.current;
    resetRetryStateAfterPlaybackStarts();
  }, [resetRetryStateAfterPlaybackStarts]);

  const queueTranscodeRetry = useCallback(
    (time) => {
      const r = retryRef.current;
      if (r.transcodeTimeout) {
        showFinalError();
        return;
      }
      if (r.hasTranscodeRetry) return;
      if (r.transcodeCount >= MAX_TRANSCODE_RETRIES) {
        showFinalError('Exceeded max transcode retries');
        updateStage(null);
        setIsLoading(false);
        setVideo((p) => ({ ...p, playing: false }));
        return;
      }

      retryRef.current.transcodeCount += 1;
      retryRef.current.hasTranscodeRetry = true;
      retryRef.current.retryTime = time;
      isFetchingRef.current = false;

      updateStage(5);

      setPlaybackStream(null);
      adapterRef.current = null;
      lastStreamTokenRef.current = null;
      setOutput(null);
      setIsLoading(true);
      streamModeRef.current = STREAM_MODE.TRANSCODE;
      setStreamMode(STREAM_MODE.TRANSCODE);
    },
    [retryRef, showFinalError, updateStage],
  );

  const retryPlaybackByCurrentLink = useCallback(
    (currentOutput = null) => {
      if (!isMountedRef.current) return;
      setIsLoading(true);

      const r = retryRef.current;
      const useTranscode = streamModeRef.current === STREAM_MODE.TRANSCODE;
      const fallbackTime = r.retryTime ?? moment(activeQueryTime).unix();

      if (typeof currentOutput === 'string') {
        if (r.hlsCount >= MAX_HLS_RETRIES) {
          retryRef.current.hlsCount = 0;

          if (useTranscode) {
            if (r.refetchCount >= MAX_TRANSCODE_REFETCH) {
              showFinalError('Transcode playback failed');
              return;
            }
            retryRef.current.refetchCount += 1;
            retryRef.current.transcodeCount = 0;
            isFetchingRef.current = false;
            setPlaybackStream(null);
            setOutput(null);
            adapterRef.current = null;
            lastStreamTokenRef.current = null;
            fetchStreamRef.current(true, STREAM_MODE.TRANSCODE);
            return;
          }

          queueTranscodeRetry(fallbackTime);
          return;
        }

        retryRef.current.hlsCount += 1;
        setOutput(null);
        const timeoutId = setTimeout(() => {
          if (isMountedRef.current) setOutput(currentOutput);
        }, 2000);
        return () => clearTimeout(timeoutId);
      }

      retryRef.current.hlsCount = 0;

      if (useTranscode) {
        if (r.transcodeCount >= MAX_TRANSCODE_RETRIES) {
          showFinalError('Transcode stream failed');
          return;
        }
        retryRef.current.transcodeCount += 1;
        isFetchingRef.current = false;
        setPlaybackStream(null);
        setOutput(null);
        adapterRef.current = null;
        lastStreamTokenRef.current = null;
        fetchStreamRef.current(true, STREAM_MODE.TRANSCODE);
        return;
      }

      queueTranscodeRetry(fallbackTime);
    },
    [
      isMountedRef,
      retryRef,
      streamModeRef,
      activeQueryTime,
      queueTranscodeRetry,
      showFinalError,
    ],
  );

  const restartPlayback = useCallback(
    (preferredMode = null) => {
      if (!isMountedRef.current) return;

      const nextMode =
        preferredMode ||
        lastPlayableModeRef.current ||
        streamModeRef.current ||
        STREAM_MODE.NORMAL;

      resetStreamState('restartPlayback');
      setVideoError(null);
      setIsLoading(true);
      streamModeRef.current = nextMode;
      setStreamMode(nextMode);
      resetRetryStateAfterPlaybackStarts();

      setTimeout(() => {
        if (isMountedRef.current) {
          fetchStreamRef.current(false, nextMode);
        }
      }, 0);
    },
    [
      isMountedRef,
      resetStreamState,
      setVideoError,
      setIsLoading,
      resetRetryStateAfterPlaybackStarts,
    ],
  );

  const retryNormalOnceOrFallbackTranscode = useCallback(
    (err, time, fallback, retryFn) => {
      const r = retryRef.current;
      if (r.hasTranscodeRetry || r.transcodeTimeout) return false;

      const errorKey = getErrorKey(err, fallback);
      const prev = r.normalMode;
      const shouldUseSingleRetry =
        prev.key === errorKey ||
        isSingleRetryNormalError(errorKey) ||
        !prev.retried;

      if (shouldUseSingleRetry && !prev.retried) {
        retryRef.current.normalMode = { key: errorKey, retried: true };
        if (typeof retryFn === 'function') {
          retryFn();
        } else {
          setTimeout(() => {
            fetchStreamRef.current(true);
          }, 0);
        }
        return true;
      }

      queueTranscodeRetry(time ?? r.retryTime);
      return true;
    },
    [retryRef, getErrorKey, isSingleRetryNormalError, queueTranscodeRetry],
  );

  const requestStreamRefetch = useCallback((reason = '') => {
    if (!fetchStreamRef.current) return;
    beeXLogger.debug('Refetching playback stream:', reason);
    isFetchingRef.current = false;
    fetchStreamRef.current(true);
  }, []);

  const fetchStream = useCallback(
    (isErrorRetry = false, preferredMode = null) => {
      if (!serial || !namespace || !autoPlay) return;
      if (isFetchingRef.current) return;

      const r = retryRef.current;
      const useTranscode =
        (preferredMode || streamModeRef.current) === STREAM_MODE.TRANSCODE;

      if (!isErrorRetry) resetNormalModeRetry();

      if (useTranscode && r.transcodeTimeout) {
        showFinalError();
        return;
      }

      isFetchingRef.current = true;
      setIsLoading(true);
      updateStage(useTranscode ? 5 : 1);
      setOutput(null);
      setPlaybackStream(null);
      adapterRef.current = null;
      lastStreamTokenRef.current = null;
      lastPlayedSecondsRef.current = videoDurationRef.current
        ? videoDurationRef.current * (video.played || 0)
        : 0;

      const fileOffset = filePlayedSecondsRef?.current || 0;
      const datePart = moment(selectedDate);

      let queryTimeMoment = datePart.clone();
      if (activeQueryTime) {
        const timePart = moment(activeQueryTime);
        queryTimeMoment.set({
          hour: timePart.hour(),
          minute: timePart.minute(),
          second: timePart.second(),
        });
      } else if (fromTime && typeof fromTime === 'string') {
        const fromParts = fromTime.split(':');
        if (fromParts.length === 3) {
          queryTimeMoment.set({
            hour: parseInt(fromParts[0], 10),
            minute: parseInt(fromParts[1], 10),
            second: parseInt(fromParts[2], 10),
          });
        }
      }

      if (fileOffset > 0) {
        queryTimeMoment.add(fileOffset, 'seconds');
      }

      const queryTimeFormatted = queryTimeMoment.format('YYYY-MM-DD HH:mm:ss');

      const params = {
        serial,
        namespace,
        queryTime: queryTimeFormatted,
        serviceId: 4,
        type: 'external',
        functions: 'transcode_h264'
      };
      // if (useTranscode) params.functions = 'transcode_h264';

      if (!isSeekingRef.current) {
        setVideo((p) => ({ ...p, played: 0, playing: true, ended: false }));
      } else {
        setVideo((p) => ({ ...p, playing: true }));
      }

      beeXLogger.debug('🚀 ~ fetchStream ~ params:', params);
      startPlaybackCamera(params)
        .then((res) => {
          beeXLogger.debug('🚀 ~ fetchStream ~ res:', res);
          isFetchingRef.current = false;
          isSeekingRef.current = false;
          if (!isMountedRef.current) return;
          retryRef.current.retryTime = moment(activeQueryTime).unix();
          updateStage(2);
          setPlaybackStream(res);
        })
        .catch((err) => {
          isFetchingRef.current = false;
          if (!isMountedRef.current) return;
          beeXLogger.error('startPlaybackCamera err:', params.functions, err);
          updateStage(null);

          if (useTranscode) {
            const isTimeout =
              err?.code === 'TIMEOUT' || err?.message === 'Request timed out';
            if (isTimeout) {
              retryRef.current.transcodeTimeout = true;
              retryRef.current.hasTranscodeRetry = true;
            }
            showFinalError();
            return;
          }

          const handled = retryNormalOnceOrFallbackTranscode(
            err,
            moment(activeQueryTime).unix(),
            'Failed to request playback stream',
          );
          if (!handled && isMountedRef.current) setIsLoading(false);
        });
    },
    [
      serial,
      namespace,
      autoPlay,
      retryRef,
      streamModeRef,
      resetNormalModeRetry,
      videoDurationRef,
      video.played,
      selectedDate,
      activeQueryTime,
      fromTime,
      toTime,
      recordId,
      isSeekingRef,
      showFinalError,
      setVideo,
      isMountedRef,
      retryNormalOnceOrFallbackTranscode,
    ],
  );

  useEffect(() => {
    fetchStreamRef.current = fetchStream;
  }, [fetchStream]);

  useEffect(() => {
    isFetchingRef.current = false;
  }, [serial, activeQueryTime, recordId, fromTime, toTime]);

  useEffect(() => {
    if (playerMode === 'file') return;
    if (!serial || !namespace || !autoPlay) return;
    beeXLogger.debug('Fetching stream:', {
      serial,
      namespace,
      queryTime: activeQueryTime,
      recordId,
      fromTime,
      toTime,
    });
    resetTranscodeRetry();
    fetchStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, serial, activeQueryTime, autoPlay, playerMode, recordId, fromTime, toTime]);

  useEffect(() => {
    if (playerMode === 'file') return;
    if (streamMode !== STREAM_MODE.TRANSCODE) return;
    if (!retryRef.current.hasTranscodeRetry) return;
    if (retryRef.current.transcodeTimeout) {
      showFinalError();
      return;
    }
    fetchStreamRef.current(false, STREAM_MODE.TRANSCODE);
  }, [streamMode, showFinalError, playerMode, retryRef]);

  useEffect(() => {
    if (!playbackStream) return;
    adapterRef.current =
      playbackStream.protocol === 'webrtc'
        ? new RTCCodecAdapter()
        : new HLSAdapter();
    return () => {
      if (adapterRef.current) {
        try {
          adapterRef.current.destroy();
        } catch (e) {
          beeXLogger.error('Error destroying adapter:', e);
        }
      }
      adapterRef.current = null;
      lastStreamTokenRef.current = null;
    };
  }, [playbackStream]);

  useEffect(() => {
    const adapter = adapterRef.current;
    if (!adapter || !playbackStream) return;
    if (lastStreamTokenRef.current === playbackStream.streamToken) return;

    lastStreamTokenRef.current = playbackStream.streamToken;

    adapter.connect(
      { ...playbackStream, streamMode: streamModeRef.current },
      { streamToken: playbackStream.streamToken },
      (out) => {
        if (!isMountedRef.current) return;
        if (!out) {
          retryNormalOnceOrFallbackTranscode(
            new Error('No stream output'),
            null,
            'No stream output',
            () => retryPlaybackByCurrentLink(null),
          );
          return;
        }
        updateStage(3);
        setIsLoading(true);
        setOutput(out);
      },
      (err) => {
        if (!isMountedRef.current) return;
        beeXLogger.error('Adapter connect error:', err);
        updateStage(null);
        if (streamModeRef.current === STREAM_MODE.TRANSCODE) {
          retryPlaybackByCurrentLink(null);
        } else {
          const handled = retryNormalOnceOrFallbackTranscode(
            err,
            null,
            'Client failed to connect stream',
            () => retryPlaybackByCurrentLink(null),
          );
          if (!handled && isMountedRef.current) setIsLoading(false);
        }
      },
    );

    return () => {
      try {
        if (adapter) adapter.destroy();
      } catch (e) {
        beeXLogger.error('Error destroying adapter in connect:', e);
      }
    };
  }, [
    playbackStream,
    streamMode,
    isMountedRef,
    retryNormalOnceOrFallbackTranscode,
    retryPlaybackByCurrentLink,
  ]);

  useEffect(() => {
    if (!(output instanceof MediaStream) || !videoRef.current) return;
    const el = videoRef.current;
    el.srcObject = output;

    let hasLoaded = false;
    let timerId = null;

    const offLoading = () => {
      if (!isMountedRef.current || hasLoaded) return;
      hasLoaded = true;
      updateStage(4);
      setIsLoading(false);
      timerId = setTimeout(() => {
        if (isMountedRef.current) updateStage(null);
      }, 1000);
    };

    const onMeta = () => {
      if (!isMountedRef.current) return;
      const w = el.videoWidth;
      if (canvasRef.current) {
        canvasRef.current.width = w;
        canvasRef.current.height = parseInt(w / (w / el.videoHeight), 10);
      }
    };

    const onTimeUpdate = () => {
      if (!isMountedRef.current) return;
      if (el.currentTime > 0) {
        markPlaybackStable();
        if (!hasLoaded) {
          offLoading();
        }
      }
      handleProgress(el.currentTime);
    };

    el.addEventListener('canplay', offLoading);
    el.addEventListener('playing', offLoading);
    el.addEventListener('loadeddata', offLoading);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('timeupdate', onTimeUpdate);

    try {
      const playPromise = el.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          beeXLogger.warn('Autoplay MediaStream prevented:', err);
        });
      }
    } catch (e) {
      beeXLogger.warn('Error calling play():', e);
    }

    return () => {
      if (timerId) clearTimeout(timerId);
      el.removeEventListener('canplay', offLoading);
      el.removeEventListener('playing', offLoading);
      el.removeEventListener('loadeddata', offLoading);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [
    output,
    isMountedRef,
    videoRef,
    canvasRef,
    handleProgress,
    markPlaybackStable,
  ]);

  useEffect(() => {
    if (typeof output !== 'string' || !playerRef.current) return;
    const root = playerRef.current;
    const v =
      typeof root.getInternalPlayer === 'function'
        ? root.getInternalPlayer()
        : root;
    if (!v || typeof v.addEventListener !== 'function') return;
    const onMeta = () => {
      if (!isMountedRef.current) return;
      const w = v.videoWidth;
      if (canvasRef.current) {
        canvasRef.current.width = w;
        canvasRef.current.height = parseInt(w / (w / v.videoHeight), 10);
      }
    };
    v.addEventListener('loadedmetadata', onMeta);
    return () => v.removeEventListener('loadedmetadata', onMeta);
  }, [output, isMountedRef, playerRef, canvasRef]);

  const handleReady = useCallback(() => {
    if (!isMountedRef.current) return;
    updateStage(4);
    setTimeout(() => {
      if (isMountedRef.current) updateStage(null);
    }, 1500);
    setIsLoading(false);
    markPlaybackStable();
  }, [isMountedRef, markPlaybackStable, updateStage]);

  const handleStart = useCallback(() => {
    if (isMountedRef.current) setIsLoading(false);
  }, [isMountedRef]);

  useEffect(() => {
    if (streamMode === STREAM_MODE.TRANSCODE) {
      setDetectedCodec('H.264');
      return;
    }

    if (output) {
      const pc = adapterRef.current?.peerConnection;
      const target = pc || output || adapterRef.current?.url || adapterRef.current;
      detectMediaCodec(target).then((res) => {
        if (res && res.codec && res.codec !== 'UNKNOWN') {
          setDetectedCodec(res.codec);
        }
      });
    } else {
      setDetectedCodec(null);
    }
  }, [output, streamMode]);

  return {
    playbackStream,
    output,
    streamMode,
    detectedCodec,
    isLoading,
    setIsLoading,
    loadingStage,
    videoError,
    setVideoError,
    resetStreamState,
    resetTranscodeRetry,
    showFinalError,
    retryPlaybackByCurrentLink,
    retryNormalOnceOrFallbackTranscode,
    requestStreamRefetch,
    restartPlayback,
    handleProgress,
    handleReady,
    handleStart,
  };
};
